import React, { useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useForm } from "react-hook-form";
import InputNumber from "src/components/InputNumber";
import ModalSide from "src/components/Modal/styles";
import CardContractItemType from "src/components/CardContractItemType";
import stringToNumber from "src/utils/stringToNumber";
import { IModalRefSubsidiaryFinancialManagement, IModalSubsidiaryFinancialManagement } from "./interface";
import { IOftba002 } from "../../interface";
import schemaAddOrChange from "./schemas";

const ModalAddOrChangeSubsidiary: React.ForwardRefRenderFunction<IModalRefSubsidiaryFinancialManagement, IModalSubsidiaryFinancialManagement> = ({
	ofa001Id
}, ref) => {
	const { translate } = useTranslation(); 
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();

	const { currentLanguage } = useTranslation();
    
	const { handleSubmit, control, register, reset, formState: {errors}, setError, clearErrors } = useForm();

	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const [subtitleModal, setSubtitleModal] = useState("");
	const onCallbackFunction = useRef<Function>(() => {});
	const [isAdd, setIsAdd] = useState(true);
	const [isFirstLevel, setIsFirstLevel] = useState(false);
	const [hasChildrens, setHasChildrens] = useState(false);
	const [oftba002, setOftba002] = useState({} as IOftba002);
	const parentNodeId = useRef<number>(0);
	const openModalAddOrChange = async (parentId: number, id?: number, subtitle?: string, firstLevel?: boolean, childrens?: boolean, onCallback?: Function) => {
		reset();
		clearErrors();
		setIsFirstLevel(firstLevel ?? false);
		setHasChildrens(childrens ?? false);
		parentNodeId.current = parentId;
		onCallbackFunction.current = onCallback || (() => {});
		if(id){
			try{
				setLoading(true);
    
				const {data} = await RCCServicesApi.getRequest({
					method: "Subsidiary/Select",
					urlParameters: {
						id,
						ofa001Id
					}
				});
				setOftba002(data);
				setSubtitleModal(`${translate("Unidade/Filial")}: ${subtitle}`);
				setIsAdd(false);
				setLoading(false);
				modalAddOrChange.current?.openModal();
			}catch (err){
				setLoading(false);
				standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
			}
			return;
		}
		setOftba002({
			ofa002Descricao: subtitle,
			ofa002Id: 0
		} as IOftba002);
		setIsAdd(true);
		modalAddOrChange.current?.openModal();
	};
	const onSubmit = async (data: any) => {
		try{	
			await schemaAddOrChange(translate).validate({...data},{
				abortEarly: false
			});

			const dataChanged = data;
			dataChanged.ofa002Implucro = stringToNumber(data.ofa002Implucro, currentLanguage);

			if (isAdd){
				await RCCServicesApi.postRequest({
					method: "Subsidiary/Insert",
					body: {
						...dataChanged,
						ofa002Idsuperior: parentNodeId.current,
						ofa002Ofa001Id: ofa001Id
					}
				});
    
				addToast({
					color: "green",
					description: translate("Unidade/Filial adicionada com sucesso"),
					title: translate("Sucesso")
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "Subsidiary/Change",
					body: {
						...dataChanged,
						ofa002Idsuperior: parentNodeId.current,
						ofa002Ofa001Id: ofa001Id
					}
				});
    
				addToast({
					color: "green",
					description: translate("Unidade/Filial alterada com sucesso"),
					title: translate("Sucesso")
				});
			}
			onCallbackFunction.current();
			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			modalAddOrChange.current?.resetLoadingButton();
			standardErrorMessages(err, translate("Erro ao salvar Unidade/Filial"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModalAddOrChange
	}));

	return (
		<Modal
			ref={modalAddOrChange}
			header={{
				title: isAdd ? translate("Adicionar") : translate("Alterar"),
				subtitle: subtitleModal,
				icon: {
					name: "building"
				}
			}}
			onSubmit={handleSubmit(onSubmit)}
			styleInline={ModalSide}
		>
			{isAdd && (
				<RCCGrid container item>
					<CardContractItemType 
						type="33"
						routeAPI="Subsidiary/GetContractItem"
						extraParams={{
							ofa001Id
						}}
					/>
				</RCCGrid>
			)}
			<input 
				type="text"
				value={oftba002.ofa002Id}
				{...register("ofa002Id")} 
				style={{ display: "none" }} 
				readOnly
			/>
			<RCCGrid container item style={isFirstLevel ? { display: "none" } : {}}>
				<RCCInput 
					name="ofa002Descricao"
					register={register}
					label={translate("Descrição")}
					maxLength={200}
					required
					defaultValue={oftba002.ofa002Descricao}
					error={errors.ofa002Descricao}
					disabled={isFirstLevel}
				/>
			</RCCGrid>
			<RCCGrid container item>
				<InputNumber 
					name="ofa002Implucro"
					control={control}
					label={translate("Impostos sobre lucro")}
					defaultValue={oftba002.ofa002Implucro}
					error={errors.ofa002Implucro}
					maxLength={5}
				/>
			</RCCGrid>
			<RCCGrid container item style={hasChildrens ? { display: "none" } : {}}>
				<RCCInput 
					name="ofa002Codinteg"
					register={register}
					label={translate("Código para integração")}
					maxLength={20}
					defaultValue={hasChildrens ? "" : oftba002.ofa002Codinteg}
					error={errors.ofa002Codinteg}
					disabled={hasChildrens}
				/>
			</RCCGrid>
		</Modal>
	);
};

export default React.forwardRef(ModalAddOrChangeSubsidiary);
